import type { FlowId } from '../api/contracts';

export interface ProcessStep {
  readonly system: string;
  readonly action: string;
  readonly explanation: string;
}
/** Un recorrido del flujo: el principal o una rama (por ejemplo, el rechazo de F11). */
export interface ProcessPath {
  readonly title: string;
  readonly steps: readonly ProcessStep[];
}
export interface ProcessReference {
  readonly id: FlowId;
  readonly title: string;
  readonly purpose: string;
  readonly trigger: string;
  readonly scope: string;
  readonly paths: readonly ProcessPath[];
}

const step = (system: string, action: string, explanation: string): ProcessStep => ({
  system,
  action,
  explanation,
});
const path = (...steps: ProcessStep[]) => ({ title: 'Recorrido principal', steps });

/** Referencia editorial: PLAN.md, documentos docs/f* y workers de cada flujo.
 * Describe el proceso de negocio; no representa la salud ni la configuración del ambiente.
 * Actualizar junto con los cambios de alcance de los flujos.
 */
export const PROCESS_REFERENCE = [
  {
    id: 'F1',
    title: 'Pedidos de tienda',
    purpose: 'Incorpora las ventas de DecoraPOS al OMS y devuelve al POS el avance del pedido.',
    trigger: 'DecoraPOS envía una venta; los eventos de Janis actualizan su seguimiento.',
    scope:
      'El alta y seguimiento usan Temporal. Requiere configurar el canal de venta de Janis y el receptor de avisos del POS.',
    paths: [
      path(
        step(
          'DecoraPOS',
          'Enviar venta',
          'Envía cliente, líneas, pagos y opciones de entrega con una referencia estable.',
        ),
        step(
          'Middleware',
          'Validar y persistir',
          'Valida el contrato, evita duplicados y registra el pedido antes de iniciar el workflow.',
        ),
        step(
          'Janis OMS',
          'Crear pedido',
          'Recibe el pedido y administra su ciclo de vida. El middleware conserva la relación entre identificadores.',
        ),
        step(
          'DecoraPOS',
          'Recibir estados',
          'El workflow procesa los avisos de Janis y notifica los cambios al POS mediante webhooks firmados.',
        ),
      ),
    ],
  },
  {
    id: 'F2',
    title: 'Clientes y pedidos en CRM',
    purpose: 'Mantiene en HubSpot el historial comercial de clientes y compras.',
    trigger:
      'Eventos de pedidos: el cliente se obtiene de la venta, no de un webhook propio de clientes.',
    scope:
      'Requiere habilitar la integración, credenciales, pipeline, etapa y propiedades únicas en HubSpot.',
    paths: [
      path(
        step(
          'Janis / DecoraPOS',
          'Identificar compra',
          'El pedido aporta los datos del cliente y de su compra.',
        ),
        step(
          'Middleware',
          'Preparar identidades',
          'Separa los contactos por RUT o correo y prepara lotes respetando los límites del destino.',
        ),
        step(
          'HubSpot',
          'Actualizar contacto',
          'Crea o actualiza el contacto mediante upsert para que un reintento no lo duplique.',
        ),
        step(
          'HubSpot',
          'Asociar negocio',
          'Registra el pedido con su identidad única y lo vincula al contacto.',
        ),
      ),
    ],
  },
  {
    id: 'F3',
    title: 'Stock desde SAP',
    purpose:
      'Publica en Janis las existencias por SKU y bodega. SAP es la autoridad del stock de origen.',
    trigger:
      'Sincronización programada o señal de cambio de stock en SAP. F15 descubre bodegas, F16 las crea en Janis y F17 reconcilia las existencias.',
    scope:
      'Requiere bodegas habilitadas y mapeadas, y SKU previamente creados por F12. La disponibilidad de webhooks SAP depende de la instancia.',
    paths: [
      path(
        step(
          'SAP B1',
          'Leer existencias',
          'Consulta un snapshot por artículo y bodega; no presupone un delta fiable por fecha.',
        ),
        step(
          'Middleware',
          'Mapear bodegas y SKU',
          'Aplica la configuración de bodegas y traduce las referencias entre sistemas.',
        ),
        step(
          'Janis OMS',
          'Publicar stock',
          'Recibe los lotes de existencias sobre los que administra su disponibilidad hacia los canales.',
        ),
      ),
    ],
  },
  {
    id: 'F4',
    title: 'Importaciones en tránsito',
    purpose: 'Informa a Janis la mercadería comprada que todavía está pendiente de recepción.',
    trigger: 'Job periódico de lectura de órdenes de compra abiertas en SAP.',
    scope:
      'Requiere habilitar la sincronización y disponer de mapeos de bodegas. Las cantidades en tránsito se distinguen del stock físico de F3.',
    paths: [
      path(
        step(
          'SAP B1',
          'Leer compras abiertas',
          'Obtiene las cantidades pendientes de recepción y las fechas previstas.',
        ),
        step(
          'Middleware',
          'Agrupar por bodega',
          'Usa RemainingOpenQuantity y separa las líneas por bodega para no duplicar lo ya recibido.',
        ),
        step(
          'Janis OMS',
          'Actualizar ingresos',
          'Publica los ingresos mediante Supplying, el recurso de abastecimiento de Janis.',
        ),
      ),
    ],
  },
  {
    id: 'F5',
    title: 'Ventas de marketplace',
    purpose: 'Convierte las ventas de Multivende en pedidos administrados por Janis.',
    trigger: 'Webhook de Multivende con el identificador de la venta.',
    scope:
      'El alta usa BullMQ. Las cancelaciones aún no están implementadas; requiere OAuth del merchant y filtro de IPs del callback.',
    paths: [
      path(
        step(
          'Multivende',
          'Avisar venta',
          'El webhook funciona como señal y no contiene la venta completa.',
        ),
        step(
          'Middleware',
          'Persistir y encolar',
          'Registra y deduplica el aviso, responde rápido y deja el procesamiento en una cola.',
        ),
        step(
          'Multivende',
          'Consultar venta completa',
          'El worker recupera cliente, artículos, descuentos y despacho para construir el pedido.',
        ),
        step(
          'Janis OMS',
          'Crear pedido',
          'Publica la venta validada y guarda la relación entre checkout y pedido para los siguientes procesos.',
        ),
      ),
    ],
  },
  {
    id: 'F6',
    title: 'Facturación electrónica',
    purpose:
      'Coordina la emisión del DTE, su registro en Janis y su publicación en Multivende cuando corresponde.',
    trigger:
      'Diseño basado en eventos de facturación de Janis; la secuencia temprana de venta web requiere definición.',
    scope:
      'Diseño objetivo pendiente de elegir el facturador. Antes de registrar el DTE en Janis debe existir la orden de venta en SAP (F10), para mantener la reserva.',
    paths: [
      path(
        step(
          'Middleware / SAP',
          'Asegurar reserva',
          'Exige la referencia de la orden de venta de SAP antes de registrar una factura en Janis.',
        ),
        step(
          'Facturador',
          'Emitir DTE',
          'El proveedor externo emite el documento tributario: Janis no emite DTE.',
        ),
        step(
          'Janis OMS',
          'Registrar factura',
          'Recibe el documento emitido mediante JEF y actualiza la facturación del pedido.',
        ),
        step(
          'Multivende',
          'Publicar documento',
          'Para una venta marketplace, adjunta el DTE al checkout correspondiente.',
        ),
      ),
    ],
  },
  {
    id: 'F7',
    title: 'Stock hacia marketplaces',
    purpose: 'Lleva la disponibilidad de Janis a los canales de venta conectados a Multivende.',
    trigger: 'Sincronización de stock de Janis hacia la bodega configurada de Multivende.',
    scope:
      'Requiere habilitación, OAuth y mapeo de bodegas. Publica availableStock; si falta, el comportamiento actual usa el stock físico como respaldo.',
    paths: [
      path(
        step(
          'Janis OMS',
          'Consultar disponible',
          'Lee las unidades disponibles para vender, descontadas las reservas y el stock de seguridad.',
        ),
        step(
          'Middleware',
          'Preparar lote',
          'Identifica cada SKU, convierte disponibles negativos a cero y prepara el lote por bodega.',
        ),
        step(
          'Multivende',
          'Actualizar stock',
          'Publica varios SKU hacia una bodega mediante Bulk update stock y respeta los límites de solicitudes.',
        ),
      ),
    ],
  },
  {
    id: 'F8',
    title: 'Precios de productos',
    purpose: 'Sincroniza los precios base de Syncro con los SKU de Janis.',
    trigger: 'Job de sincronización de precios.',
    scope:
      'Requiere habilitación y credenciales de Syncro. El precio pertenece al producto: sus versiones comparten precio.',
    paths: [
      path(
        step(
          'Syncro',
          'Leer precios',
          'Obtiene el precio comercial, el porcentaje de descuento y el costo de las versiones.',
        ),
        step(
          'Middleware',
          'Calcular por SKU',
          'Traduce el precio a cada versión vendible y omite precios de lista o costo nulos o cero para no borrar valores.',
        ),
        step(
          'Janis OMS',
          'Actualizar precio base',
          'Publica lotes de hasta 500 mediante base-price-batch; no modifica listas de precios por canal.',
        ),
      ),
    ],
  },
  {
    id: 'F9',
    title: 'Picking y despacho',
    purpose:
      'Coordina la preparación física en Altanet con el control, packing y despacho en Janis.',
    trigger: 'Avisos de estado del picking de Altanet y avance del pedido en Janis.',
    scope:
      'La integración depende del contrato de Altanet. SAP conserva la creación de órdenes en el WMS; el callback actual de recepción y ajustes hacia SAP se mantiene.',
    paths: [
      path(
        step(
          'SAP → Altanet',
          'Crear y preparar orden',
          'La interfaz existente crea la orden. Altanet ejecuta el picking físico.',
        ),
        step(
          'Middleware',
          'Recibir picking',
          'Recibe el aviso en un endpoint propio, lo registra y evita duplicados.',
        ),
        step(
          'Janis OMS',
          'Controlar y despachar',
          'El pedido avanza al control y packing; el despacho y la entrega se administran desde Janis.',
        ),
        step(
          'Altanet WMS',
          'Cerrar orden',
          'El middleware comunica el cierre de la orden y el documento comercial de salida con la guía emitida.',
        ),
      ),
    ],
  },
  {
    id: 'F10',
    title: 'Documentos de venta en SAP',
    purpose:
      'Registra la venta y sus documentos contables en SAP, conservando la reserva y la relación entre líneas.',
    trigger:
      'Actualmente order.invoiced crea la orden de venta. El ciclo objetivo incorpora el DTE de F6 y la entrega de Janis.',
    scope:
      'La orden de venta está implementada. La factura de reserva y entrega están pendientes de completar el circuito con F6 y WMS. El registro del DTE en Janis exige primero la orden SAP.',
    paths: [
      path(
        step(
          'Middleware',
          'Relacionar pedido',
          'Recupera el pedido de Janis y consulta la identidad estable para evitar documentos SAP duplicados.',
        ),
        step(
          'SAP B1',
          'Crear orden de venta',
          'La orden compromete stock y guarda las referencias de sus líneas.',
        ),
        step(
          'SAP B1 · pendiente',
          'Factura de reserva',
          'En el circuito completo, incorpora el folio y URL del DTE producido por F6.',
        ),
        step(
          'SAP B1 · pendiente',
          'Entrega de mercadería',
          'Al entregar al cliente, referencia las líneas procesadas por WMS, rebaja stock y cierra la reserva correspondiente.',
        ),
      ),
    ],
  },
  {
    id: 'F11',
    title: 'Devoluciones',
    purpose:
      'Abre una solicitud de devolución y coordina su resolución según la política del flujo RMS de Janis.',
    trigger: 'DecoraPOS solicita devolver un pedido que ya existe en Janis.',
    scope:
      'Hoy se abre la solicitud en Janis y se lee su política. Los tramos SAP, WMS, nota de crédito y aviso de vuelta al POS están pendientes; no se decide la política en el middleware.',
    paths: [
      path(
        step(
          'DecoraPOS',
          'Solicitar devolución',
          'Envía la referencia del pedido y las líneas que se quieren devolver.',
        ),
        step(
          'Middleware',
          'Resolver pedido',
          'Encuentra el pedido original en Janis y valida las referencias.',
        ),
        step(
          'Janis RMS',
          'Abrir solicitud',
          'Registra la devolución y consulta si su política exige aprobación, picking y nota de crédito.',
        ),
      ),
      {
        title: 'Recepción conforme · diseño objetivo',
        steps: [
          step(
            'SAP → WMS',
            'Solicitar recepción',
            'SAP crea la solicitud y su interfaz genera la orden de recepción física.',
          ),
          step(
            'WMS → SAP',
            'Confirmar recepción',
            'La recepción llega por la interfaz actual; SAP completa la devolución.',
          ),
          step(
            'Middleware / Janis',
            'Informar resolución',
            'Comunica la recepción a Janis y coordina la nota de crédito con el facturador y SAP según la política.',
          ),
        ],
      },
      {
        title: 'Rechazo · diseño objetivo',
        steps: [
          step(
            'Janis',
            'Rechazar solicitud',
            'Servicio al cliente registra el rechazo cuando el producto no es aceptado.',
          ),
          step(
            'SAP / WMS',
            'Cancelar pendientes',
            'Debe cancelar la solicitud y la orden de recepción, sin nota de crédito. Las operaciones de cancelación requieren confirmación.',
          ),
        ],
      },
    ],
  },
  {
    id: 'F12',
    title: 'Catálogo para Janis',
    purpose:
      'Crea en Janis los productos y SKU de Syncro que necesitan los flujos de stock, precios y ventas.',
    trigger: 'Carga manual de catálogo, con opción de simulación (dry-run).',
    scope:
      'Carga Amoblé y Forastero según el atributo open. Otras marcas se omiten con motivo registrado. La carga continua requiere definir el tratamiento de bajas.',
    paths: [
      path(
        step(
          'Syncro',
          'Leer catálogo',
          'Recupera versiones vendibles, sus productos y categorías; la versión es el SKU.',
        ),
        step(
          'Middleware',
          'Validar y mapear',
          'Resuelve marcas, categorías y unidades de medida. En simulación prepara el resultado sin publicar.',
        ),
        step(
          'Janis OMS',
          'Marcas y categorías',
          'Crea o actualiza primero las referencias que necesitan los productos.',
        ),
        step(
          'Janis OMS',
          'Productos → SKU',
          'Publica productos y luego sus SKU por lotes idempotentes; relee lo publicado para verificarlo.',
        ),
      ),
    ],
  },
  {
    id: 'F13',
    title: 'Catálogo para DecoraPOS',
    purpose:
      'Entrega a DecoraPOS los SKU, nombres, precios de lista y categorías de los productos elegibles de Syncro.',
    trigger: 'DecoraPOS consulta GET /api/catalog, opcionalmente por marca y canal.',
    scope:
      'Lectura bajo demanda, sin publicación en Janis ni caché. Requiere credenciales de Syncro y confirmar la lista de precios CLP con IVA. Las promociones se resuelven en el POS.',
    paths: [
      path(
        step(
          'DecoraPOS',
          'Solicitar catálogo',
          'El POS inicia una consulta autenticada al middleware.',
        ),
        step(
          'Syncro',
          'Leer productos y precios',
          'El middleware recorre las páginas de versiones y consulta sus productos para obtener los precios de lista.',
        ),
        step(
          'Middleware',
          'Validar catálogo',
          'Filtra estado, marca y canal; comprueba identidad, precio y paginación. Una lectura incompleta produce un error visible.',
        ),
        step(
          'DecoraPOS',
          'Recibir catálogo',
          'Devuelve los productos elegibles y sus precios. Esta lectura no crea SKU en Janis ni incluye disponibilidad de stock.',
        ),
      ),
    ],
  },
  {
    id: 'F14',
    title: 'Disponibilidad para DecoraPOS',
    purpose:
      'Entrega al POS una lectura de disponibilidad SAP por SKU y bodega para consultar existencias en tienda.',
    trigger: 'DecoraPOS consulta GET /api/stock/availability con bodega y SKU.',
    scope:
      'Consulta bajo demanda, separada de la publicación SAP → Janis de F3. La disponibilidad es max(0, InStock − Committed); excluye las reservas de Janis y no reserva unidades.',
    paths: [
      path(
        step('DecoraPOS', 'Consultar existencias', 'Solicita una bodega SAP y hasta 100 SKU.'),
        step(
          'SAP B1',
          'Leer posiciones',
          'Consulta las existencias y el stock comprometido de la bodega con una lectura completa.',
        ),
        step(
          'Middleware',
          'Calcular disponibilidad',
          'Resta compromisos y limita el resultado a cero. Distingue posiciones desconocidas y lecturas vencidas de un stock conocido en cero.',
        ),
        step(
          'DecoraPOS',
          'Recibir disponibilidad',
          'Devuelve cantidades, estado y fecha de observación. No escribe stock en SAP ni en Janis.',
        ),
      ),
    ],
  },
  {
    id: 'F15',
    title: 'Descubrimiento de bodegas',
    purpose: 'Registra en el middleware las bodegas que existen en SAP y sus nombres.',
    trigger: 'Un operador ejecuta POST /api/stock/warehouses/sync.',
    scope:
      'Las nuevas bodegas quedan deshabilitadas y sin mapeo Janis. Conserva los mapeos y la habilitación de las conocidas; no crea bodegas en Janis.',
    paths: [
      path(
        step(
          'Operador',
          'Solicitar descubrimiento',
          'Inicia una corrida de lectura del catálogo de bodegas.',
        ),
        step('SAP B1', 'Leer bodegas', 'Recorre el catálogo completo y valida la respuesta.'),
        step(
          'Middleware',
          'Registrar catálogo',
          'Agrega las nuevas bodegas y actualiza nombres sin alterar la configuración operativa existente.',
        ),
      ),
    ],
  },
  {
    id: 'F16',
    title: 'Alta de bodegas en Janis',
    purpose:
      'Crea o recupera la ubicación de Janis correspondiente a una bodega SAP ya descubierta.',
    trigger: 'Un operador ejecuta POST /api/stock/warehouses/{code}/janis.',
    scope:
      'Requiere F15 y los datos de ubicación exigidos por Janis. Crear una bodega no la habilita automáticamente para publicar stock.',
    paths: [
      path(
        step(
          'SAP B1',
          'Leer bodega',
          'Comprueba la bodega seleccionada y prepara sus datos de ubicación.',
        ),
        step(
          'Janis OMS',
          'Consultar o crear ubicación',
          'Busca por referencia antes de crear para evitar duplicados durante reintentos.',
        ),
        step(
          'Middleware',
          'Guardar y verificar mapeo',
          'Conserva la relación entre SAP y Janis y relee la ubicación para confirmar que tenga almacén.',
        ),
      ),
    ],
  },
  {
    id: 'F17',
    title: 'Reconciliación de stock',
    purpose:
      'Detecta diferencias entre el stock que se publicó en Janis y lo que Janis tiene registrado.',
    trigger: 'Job programado de reconciliación para las bodegas habilitadas y mapeadas.',
    scope:
      'Compara stock físico, no disponible. No publica existencias: marca diferencias para que una siguiente corrida de F3 vuelva a publicarlas.',
    paths: [
      path(
        step(
          'Janis OMS',
          'Leer stock físico',
          'Recorre las posiciones de cada bodega habilitada y las traduce a sus referencias SAP.',
        ),
        step(
          'Middleware',
          'Comparar publicación',
          'Contrasta las existencias con el último estado publicado. Una bodega que falla queda fuera de la comparación.',
        ),
        step(
          'Middleware',
          'Marcar diferencias',
          'Invalida las posiciones divergentes del estado publicado para que F3 las reenvíe en el siguiente snapshot.',
        ),
      ),
    ],
  },
  {
    id: 'F18',
    title: 'Disponibilidad vendible para DecoraPOS',
    purpose:
      'Entrega al POS la disponibilidad que Janis calcula para todos los canales, descontando las reservas de la web y del propio POS.',
    trigger: 'DecoraPOS consulta POST /api/decorapos/inventory/availability con SKU y bodegas SAP.',
    scope:
      'Lectura bajo demanda del disponible de Janis (stock − stock de seguridad − reservado). Sirve para mostrar; no retiene unidades. Retener es crear el pedido con retención (F1).',
    paths: [
      path(
        step('DecoraPOS', 'Consultar disponibilidad', 'Envía hasta 100 SKU y 10 bodegas SAP.'),
        step(
          'Middleware',
          'Traducir bodegas',
          'Resuelve cada bodega SAP a su referencia en Janis. Las que no tienen mapeo se informan como no mapeadas, sin consultar.',
        ),
        step(
          'Janis OMS',
          'Leer posiciones',
          'Lee las posiciones por referencia de SKU y bodega, en lotes de 50 SKU.',
        ),
        step(
          'DecoraPOS',
          'Recibir disponibilidad',
          'Una fila por cada par pedido. Una posición que Janis no tiene es desconocida, nunca cero.',
        ),
      ),
    ],
  },
  {
    id: 'F19',
    title: 'Recepciones de reposición en tienda',
    purpose:
      'Registra lo que una tienda recibió de una reposición, como total acumulado por línea, para contabilizar la diferencia en SAP sin duplicarla.',
    trigger: 'DecoraPOS envía POST /api/decorapos/inventory/receipts.',
    scope:
      'El middleware registra el acumulado y calcula el delta. No mueve stock en Janis ni en el WMS: el stock llega a Janis sólo desde SAP (F3). La contabilización en SAP está bloqueada hasta definir su documento.',
    paths: [
      path(
        step(
          'DecoraPOS',
          'Informar recepción',
          'Envía el total recibido por línea de la reposición.',
        ),
        step(
          'Middleware',
          'Registrar acumulado',
          'Rechaza retrocesos, reconoce reenvíos y guarda el delta pendiente de contabilizar.',
        ),
      ),
    ],
  },
  {
    id: 'F20',
    title: 'Catálogo Syncro hacia HubSpot',
    purpose:
      'Publica la ficha CRM de Syncro como Product en HubSpot (upsert por hs_sku) y archiva bajas.',
    trigger: 'Webhook Syncro product.saved / archive hacia el destino HubSpot.',
    scope:
      'Tercer destino del webhook Syncro, aparte de F12 y F13. Apagado con HUBSPOT_CATALOG_SYNC_ENABLED. No crea Products desde pedidos (F2).',
    paths: [
      path(
        step('Syncro', 'Avisar SKU', 'Señal delgada: tipo, acción y sku.'),
        step('Middleware', 'Leer crm_catalog', 'Fetch-back de la ficha CRM por SKU.'),
        step('HubSpot', 'Upsert o archive', 'Product por hs_sku; archive solo en este destino.'),
      ),
    ],
  },
  {
    id: 'F21',
    title: 'Reservas de proyectos Goodworking',
    purpose:
      'Reserva stock para un proyecto B2B del CRM con una orden de venta en SAP (NumAtCard CRM_<proyecto>), sin impuesto y con cada línea en su bodega de origen.',
    trigger:
      'Goodworking envía POST /api/goodworking/reservations para crear, …/modifications para fijar cantidades finales y …/close para cerrar; consulta el resultado con el GET de la reserva.',
    scope:
      'Asíncrono: cada comando se acusa con 202 y lo procesa el worker de F21, una operación a la vez por reserva. Apagado con GOODWORKING_RESERVATIONS_ENABLED. Las entregas van en F22; no reemplaza la orden de venta.',
    paths: [
      {
        title: 'Crear la reserva',
        steps: [
          step(
            'Goodworking',
            'Pedir la reserva',
            'Proyecto, cliente SAP y líneas con SKU, bodega y cantidad.',
          ),
          step(
            'SAP B1',
            'Buscar por NumAtCard',
            'Adopta la orden viva con el mismo contenido; una anulada no se recrea y otro contenido abre incidente.',
          ),
          step(
            'SAP B1',
            'Crear orden de venta',
            'Compromete el stock en la bodega de cada línea; después se relee con sus líneas.',
          ),
        ],
      },
      {
        title: 'Modificar la reserva',
        steps: [
          step(
            'Goodworking',
            'Fijar cantidades',
            'Cantidad final absoluta por SKU; 0 cierra la línea.',
          ),
          step(
            'Middleware',
            'Validar contra lo leído',
            'Relee la orden: nunca más que lo reservado ni reabrir una línea cerrada.',
          ),
          step(
            'SAP B1',
            'Actualizar líneas',
            'Rebaja o cierra líneas y vuelve a leer para confirmar; si todas quedan cerradas, la orden se cierra sola.',
          ),
        ],
      },
      {
        title: 'Cerrar la reserva',
        steps: [
          step('Goodworking', 'Cerrar', 'Cancelación o proyecto terminado.'),
          step(
            'SAP B1',
            'Cerrar orden de venta',
            'Libera lo comprometido que quede abierto y se relee para confirmar el cierre.',
          ),
        ],
      },
    ],
  },
  {
    id: 'F22',
    title: 'Entregas de proyectos Goodworking',
    purpose:
      'Lleva a BOD27 la mercadería de un proyecto reservado en F21: crea en SAP la solicitud de traslado (ST) TRAS_PROY_G<n> desde BOD1 o BOD17 y después rebaja la orden de venta de la reserva por lo que sale de ella.',
    trigger:
      'Goodworking envía POST /api/goodworking/deliveries con los productos y cantidades de la entrega y consulta su avance con GET /api/goodworking/deliveries/{key}; las revisiones demoradas y la conciliación de F22 releen la ST.',
    scope:
      'Asíncrono: el pedido se acusa con 202 y lo procesa el worker de F22 con el candado de la reserva; espera a que no haya una modificación en curso. El middleware nunca crea los traslados a BOD27 (los crea el WMS) y no reintenta a ciegas una ST en duda: queda en pausa con alarma. Apagado con GOODWORKING_DELIVERIES_ENABLED y cerrado en producción.',
    paths: [
      {
        title: 'Pedir la entrega',
        steps: [
          step(
            'Goodworking',
            'Pedir la entrega',
            'Clave <reserva>-ENT-<n>, fecha de despacho, dirección, contacto y la cantidad por SKU; el middleware calcula cuánto sale de la orden de venta y cuánto de lo pendiente.',
          ),
          step(
            'Middleware',
            'Validar contra la reserva',
            'Relee la orden: una sola bodega de origen (BOD1 o BOD17), líneas abiertas y nunca más que lo reservado.',
          ),
          step(
            'SAP B1',
            'Crear la solicitud de traslado',
            'Busca primero por folio y adopta la que calza; si no hay, la crea de la bodega de origen a BOD27 y la relee.',
          ),
          step(
            'SAP B1',
            'Rebajar la orden de venta',
            'Después de la ST, baja las líneas por lo que sale de ellas y relee para confirmar. Con la ST creada, el pedido ya no se rechaza.',
          ),
        ],
      },
      {
        title: 'Seguir la solicitud de traslado',
        steps: [
          step(
            'Middleware',
            'Revisar la ST',
            'Por defecto a los 30 minutos y después doblando la espera hasta 6 horas, mientras no llegue a BOD27; una relectura más 24 horas después del final.',
          ),
          step(
            'SAP B1',
            'Leer la ST y sus traslados',
            'Lo abierto por línea y los traslados basados en ella; si las cantidades y el estado no coinciden, abre una alarma.',
          ),
          step(
            'Goodworking',
            'Consultar el avance',
            'El GET del pedido muestra lo trasladado y lo abierto sin llamar a SAP. Lo que faltó se pide en una entrega nueva.',
          ),
        ],
      },
    ],
  },
  {
    id: 'F23',
    title: 'Catálogo para Goodworking',
    purpose:
      'Entrega al CRM de Goodworking los SKU, nombres, precios de lista y categorías de los productos elegibles de Syncro, con el mismo contrato que DecoraPOS.',
    trigger:
      'Goodworking pide el catálogo completo con POST /api/goodworking/catalog/sync-jobs y consulta su estado con el GET de la corrida; el aviso de producto de Syncro dispara la entrega por producto.',
    scope:
      'Separado de F13: tabla, cola, candado y recuperación propios, así que un CRM caído no atrasa a DecoraPOS. El aviso de Syncro solo llega con GOODWORKING_CATALOG_SYNC_ENABLED; la corrida completa responde 503 sin el receptor o la llave del CRM.',
    paths: [
      {
        title: 'Catálogo completo',
        steps: [
          step(
            'Goodworking',
            'Pedir el catálogo',
            'El CRM genera un syncId y lo reusa al reintentar; una sola corrida de Goodworking activa a la vez.',
          ),
          step(
            'Syncro',
            'Leer productos y precios',
            'Recorre todas las versiones vendibles con la lista de precios del POS y guarda una foto del catálogo.',
          ),
          step(
            'Goodworking',
            'Entregar e importar',
            'POST al receptor del CRM; los reintentos entregan la misma foto. Solo el acuse 200 del CRM deja la corrida importada.',
          ),
        ],
      },
      {
        title: 'Aviso de producto',
        steps: [
          step(
            'Syncro',
            'Avisar SKU',
            'Señal delgada; Goodworking es el último destino del aviso.',
          ),
          step(
            'Syncro',
            'Releer el producto',
            'Resuelve el producto del SKU y relee todas sus versiones vigentes.',
          ),
          step(
            'Goodworking',
            'Entregar el producto',
            'Upsert por SKU en el CRM con un syncId nuevo por intento; un rechazo o un CRM caído reintenta en la cola de F23.',
          ),
        ],
      },
    ],
  },
  {
    id: 'F24',
    title: 'Disponibilidad SAP para Goodworking',
    purpose:
      'Entrega al CRM de Goodworking el stock libre de SAP por SKU y bodega para armar las reservas de los proyectos.',
    trigger: 'Goodworking consulta GET /api/goodworking/stock/availability con bodega y SKU.',
    scope:
      'La respuesta de F14, con la llave del sistema goodworking y su propio flujo. Con 20 SKU distintos o menos y códigos seguros lee cada SKU con la consulta guardada mwF24Stock, hasta 4 a la vez; si no, la lectura completa de F14. La disponibilidad es max(0, InStock − Committed); excluye las reservas de Janis y no reserva unidades. Apagado con GOODWORKING_STOCK_ENABLED.',
    paths: [
      path(
        step('Goodworking', 'Consultar existencias', 'Solicita una bodega SAP y hasta 100 SKU.'),
        step(
          'SAP B1',
          'Leer posiciones',
          'Consulta las existencias y el stock comprometido de cada SKU en la bodega con mwF24Stock, una llamada por SKU, una tras otra. Con más de 20 SKU, o un código fuera del patrón seguro, usa la lectura completa de F14.',
        ),
        step(
          'Middleware',
          'Calcular disponibilidad',
          'Resta compromisos y limita el resultado a cero. Distingue posiciones desconocidas y lecturas vencidas de un stock conocido en cero.',
        ),
        step(
          'Goodworking',
          'Recibir disponibilidad',
          'Devuelve cantidades, estado y fecha de observación. No escribe stock en SAP ni en Janis.',
        ),
      ),
    ],
  },
  {
    id: 'F25',
    title: 'Entrega SAP después de Janis',
    purpose:
      'Crea en SAP la entrega de mercadería de las órdenes del middleware después de que Janis facturó y descontó el físico, para que la unidad no se descuente dos veces.',
    trigger:
      'order.invoiced de Janis (vía F10), las revisiones demoradas mientras el descuento no se ve y la reconciliación de las entregas.',
    scope:
      'Apagado con MW_SAP_DELIVERY_ENABLED. Vale para el despacho desde el CD y cada hijo de una venta agrupada; las líneas de tienda y la guía 52 vienen después. Mientras falta la entrega, F3 la resta de lo que publica.',
    paths: [
      path(
        step(
          'Janis',
          'Facturar y descontar',
          'El pedido llega a invoiced, la reserva pasa a confirmed y Janis resta el físico.',
        ),
        step(
          'Middleware',
          'Planificar la entrega',
          'Con el descuento visto, toma lo pickeado y su DESPACHO de line_xref, lo limita a lo abierto en la factura de reserva y guarda la entrega planificada.',
        ),
        step(
          'SAP B1',
          'Crear la entrega',
          'En la serie primaria, con Comments MW_DELIV_<id> como clave: una que ya existe se adopta y una anulada no se recrea.',
        ),
        step(
          'Middleware',
          'Cerrar la ventana de F3',
          'Registrada la entrega, F3 deja de restarla y publica lo de SAP.',
        ),
      ),
    ],
  },
] as const satisfies readonly ProcessReference[];

/**
 * La guía de un flujo, o `null` si no hay flujo o la guía no lo conoce.
 *
 * Recibe `string` y no `FlowId` porque el flujo llega de datos (un mensaje, una
 * corrida o la query string) y puede faltar: quien pregunta decide qué mostrar
 * sin guía, en vez de recibir la de otro flujo por descarte.
 */
export function findProcessReference(flow: string | null | undefined): ProcessReference | null {
  if (!flow) {
    return null;
  }
  return PROCESS_REFERENCE.find((process) => process.id === flow) ?? null;
}

/**
 * Los flujos por los que pasa una corrida, sin repetir: primero el de la corrida
 * y después los de sus pasos, en su orden.
 *
 * Una corrida puede cruzar flujos: una carga de catálogo publica en Janis (F12)
 * y en DecoraPOS (F13) bajo el mismo correlation id, y la API la informa como
 * de F12. Quedarse solo con ese dejaría sin guía a los pasos de F13, justo a los
 * que se llega pinchando "F13" en el listado de mensajes.
 */
export function flowsOfRun(
  run: {
    readonly flow: string | null;
    readonly nodes: readonly { readonly flow: string | null }[];
  } | null,
): readonly string[] {
  if (run === null) {
    return [];
  }
  const flows = [run.flow, ...run.nodes.map((node) => node.flow)];
  return [...new Set(flows.filter((flow): flow is string => flow !== null && flow !== ''))];
}
